import React, { useEffect, useState, useContext } from "react";
import { useAuth } from "../../contexts/auth";
import { Grid, Typography, CircularProgress, Box } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useNavigate } from "react-router-dom";
import Donut from "../../components/charts/donuts";
import BarStyle from "../../components/charts/bar1";
import VBarStyle from "../../components/charts/bar2";
import BarTopStyle from "../../components/charts/bar3";
import BarLowStyle from "../../components/charts/bar4";
import "./salon.style.scss";
import { toast } from "react-hot-toast";
import { baseAxios, Context } from "../../utils/config";

export default function SalonDashboard() {
  const { user } = useAuth();
  const { location: globalLocation } = useContext(Context);
  const navigate = useNavigate();

  // JD-293: Use the globally selected location. The gateway endpoint
  // requires location_id in the POST body (it doesn't read x-location-id header).
  const effectiveLocationId = (globalLocation?.location_id && globalLocation.location_id !== -1)
    ? globalLocation.location_id
    : user.location_id;
  const isAllLocations = globalLocation?.location_id === -1;

  const [valueOnHand, setValueOnHand] = useState(0.000001);
  const [valueUsed, setValueUsed] = useState(0);
  const [valueSpending, setValueSpending] = useState([]);
  const [valueConsumed, setValueConsumed] = useState([]);
  const [valueTopItems, setValueTopItems] = useState([]);
  const [valueRunningLow, setValueRunningLow] = useState([]);
  const [valueCSRatio, setValueCSRatio] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // "All Locations" not supported by this legacy gateway — skip fetch
    if (isAllLocations) {
      setLoading(false);
      return;
    }

    const url = "gateway/ebsb8f78ee"; // SalonInventoryOnHand lambda
    const days = 7;

    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await baseAxios.post(url, {
          location_id: effectiveLocationId,
          days: days,
        });

        setValueOnHand(result.data?.value_on_hand ?? 0);
        setValueUsed(result.data?.value_used ?? 0);
        setValueSpending(result.data?.stylist_spending || []);
        setValueConsumed(result.data?.most_consumed_product || []);
        setValueTopItems(result.data?.top_inventory_items || []);
        setValueRunningLow(result.data?.product_running_low || []);
        setValueCSRatio(result.data?.cs_ratio || 0);
      } catch (error) {
        toast.error(error?.message || "Error fetching data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [effectiveLocationId, isAllLocations]);

  const formatLabel = (value) => {
    if (Number.isNaN(value)) {
      return "$0";
    }
    const formattedNumber = value.toLocaleString();
    return `$${formattedNumber}`;
  };

  if (isAllLocations) {
    return (
      <div className="salon-dashboard-wrapper">
        <h2 className={"content-block"}>Pulse</h2>
        <div className={"responsive-paddings"}>
          <Box
            sx={{
              minHeight: "calc(100vh - 190px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: { xs: 2, md: 4 },
              py: { xs: 4, md: 6 },
              backgroundColor: "background.paper",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 560,
                textAlign: "center",
                px: { xs: 2, sm: 4 },
                py: { xs: 4, sm: 5 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  mx: "auto",
                  mb: 2.5,
                  borderRadius: "50%",
                  backgroundColor: "background.paper",
                  color: "#cbaa6f",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px solid #000",
                  boxShadow: "none",
                }}
              >
                <LocationOnIcon sx={{ fontSize: 32 }} />
              </Box>
              <Typography variant="h5" fontWeight={600} color="text.primary">
                Select a Location
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  mt: 1.25,
                  maxWidth: 520,
                  lineHeight: 1.65,
                  fontWeight: 400,
                }}
              >
                Pulse shows inventory and usage metrics for one salon at a
                time. Choose a specific location from the header selector to
                continue.
              </Typography>
            </Box>
          </Box>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="salon-dashboard-wrapper">
        <h2 className={"content-block"}>Pulse</h2>
        <div className={"responsive-paddings"}>
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        </div>
      </div>
    );
  }

  return (
    <div className="salon-dashboard-wrapper loading">
      <h2 className={"content-block"}>Pulse</h2>
      <div className={"responsive-paddings"}>
        <Grid
          container
          sx={{ textAlign: "center" }}
          justifyContent="center"
          alignItems="flex-start"
        >
          <Grid item xs={12}>
            <div className="top-charts-row">
              <div className="top-chart-section top-chart-section--inventory">
                <div className="period-label" aria-hidden="true">
                  &nbsp;
                </div>
                <div className="chart-title">INVENTORY INVESTMENT</div>
                <Grid container className="chart-panel">
                  <Grid item lg={6} md={6} sm={6} xs={12} className="pl-5p">
                    <Donut
                      color="#cbaa6f"
                      value={valueOnHand}
                      total={valueOnHand}
                      label={formatLabel(valueOnHand)}
                      title="On Hand"
                      onClick={() =>
                        navigate("/inventory", {
                          state: {
                            location_name:
                              globalLocation?.location_name ||
                              user.location_name ||
                              "Fairfax",
                          },
                        })
                      }
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={6} xs={12} className="pr-5p">
                    <Donut
                      color="#cbaa6f"
                      value={valueUsed}
                      total={valueOnHand}
                      label={formatLabel(valueUsed)}
                      title="Used"
                      onClick={() =>
                        navigate("/reports-sql", {
                          state: {
                            location_name:
                              globalLocation?.location_name ||
                              user.location_name ||
                              "Fairfax",
                          },
                        })
                      }
                    />
                  </Grid>
                </Grid>
              </div>

              <div className="top-chart-section top-chart-section--cs">
                <div className="period-label" aria-hidden="true">
                  &nbsp;
                </div>
                <div className="chart-title">CS RATIO</div>
                <div className="chart-panel px-5p">
                  <Donut
                    color="#cbaa6f"
                    value={valueCSRatio / 1}
                    total={100}
                    label="Color to"
                    title="Services Ratio"
                    noValue={true}
                    onClick={() =>
                      navigate("/rolled-reports-sql", {
                        state: {
                          location_name:
                            globalLocation?.location_name ||
                            user.location_name ||
                            "Fairfax",
                        },
                      })
                    }
                  />
                </div>
              </div>

              <div className="top-chart-section top-chart-section--spending">
                <div className="period-label">TIME PERIOD: PREVIOUS WEEK</div>
                <div className="chart-title">
                  STYLIST SPENDING VS TRANSACTIONS VS AVG
                </div>
                <div className="chart-panel">
                  <BarStyle
                    value={valueSpending}
                    onClick={() =>
                      navigate("/stylist-spend-sql", {
                        state: {
                          location_name:
                            globalLocation?.location_name ||
                            user.location_name ||
                            "Fairfax",
                        },
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </Grid>
          <Grid item xs={12}>
            <div className="bottom-charts-row">
              <div className="bottom-chart-section">
                <div className="chart-title">MOST CONSUMED PRODUCTS</div>
                <div className="chart-panel">
                  <VBarStyle
                    value={valueConsumed}
                    onClick={() =>
                      navigate("/reporting", {
                        state: {
                          location_name:
                            globalLocation?.location_name ||
                            user.location_name ||
                            "Fairfax",
                        },
                      })
                    }
                  />
                </div>
              </div>
              <div className="bottom-chart-section">
                <div className="chart-title">TOP INVENTORY ITEMS</div>
                <div className="chart-panel">
                  <BarTopStyle
                    value={valueTopItems}
                    onClick={() =>
                      navigate("/inventory", {
                        state: {
                          location_name:
                            globalLocation?.location_name ||
                            user.location_name ||
                            "Fairfax",
                        },
                      })
                    }
                  />
                </div>
              </div>
              <div className="bottom-chart-section">
                <div className="chart-title">PRODUCTS RUNNING LOW</div>
                <div className="chart-panel">
                  <BarLowStyle
                    value={valueRunningLow}
                    onClick={() =>
                      navigate("/reorder", {
                        state: {
                          location_name:
                            globalLocation?.location_name ||
                            user.location_name ||
                            "Fairfax",
                        },
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </Grid>
        </Grid>
      </div>
    </div>
  );
}
